import { logger } from "~/lib/logger/logger";
import { env } from "~/config/env";
import type { CMSLink } from "~/types/cms";

/**
 * Reference value structure from CMS
 */
export interface CMSReferenceValue {
  id: string;
  slug?: string;
  title?: string;
  [key: string]: unknown;
}

/**
 * Reference structure from CMS links
 */
export interface CMSReference {
  relationTo: string;
  value: string | CMSReferenceValue;
}

/**
 * Resolves a CMS reference to a URL path.
 *
 * @param reference - The CMS reference object containing relationTo and value
 * @returns The resolved URL path, or "#" if unresolvable
 */
export function resolveReferenceUrl(reference: CMSReference | undefined | null): string {
  if (!reference) return "#";

  // If value is just an ID string (not populated), we can't resolve it
  if (typeof reference.value === "string") {
    logger.debug("Reference not populated (ID only):", reference.value);
    return "#";
  }

  const { relationTo, value } = reference;

  // Handle documents - resolve to file download URL (documents have no slug)
  if (relationTo === "documents") {
    const fileUrl = value.url as string | undefined;
    if (fileUrl) {
      return fileUrl.startsWith("http") ? fileUrl : `${env.cmsApiUrl}${fileUrl}`;
    }
    return "#";
  }

  const slug = value.slug;

  if (!slug) {
    logger.debug("Reference has no slug:", reference);
    return "#";
  }

  // "home" slug maps to the root route
  if (slug === "home") return "/";

  return `/${slug}`;
}

/**
 * Resolves a CMS link to a URL
 *
 * IMPORTANT: This function strictly respects the `type` field:
 * - If type is "custom", it ONLY uses the `url` field (ignores `reference`)
 * - If type is "reference", it ONLY uses the `reference` field (ignores `url`)
 *
 * This prevents issues when a link is changed from "custom" to "reference" in the CMS
 * and the old `url` value remains populated but should be ignored.
 *
 * @param link - The CMS link object
 * @returns The resolved URL
 */
export function resolveCMSLink(link: CMSLink, context?: string): string {
  if (link.type === "custom" && link.url) {
    return link.url;
  }

  if (link.type === "reference" && link.reference) {
    return resolveReferenceUrl(link.reference as CMSReference);
  }

  const prefix = context ? `Unknown link type or missing data [${context}]:` : "Unknown link type or missing data:";
  logger.warn(prefix, link);
  return "#";
}
